<template>
  <v-app>
    <AppWrapperView>
      <v-main>
        <v-container class="pa-8" fluid>
          <div class="mb-4">
            <a class="text-primary text-body-2" style="cursor: pointer;" @click="backToList">‹ {{ $t('labelStock.label.backToList') }}</a>
          </div>
          <v-alert v-if="flash" type="success" density="compact" closable class="mb-4" :text="flash" @click:close="flash = ''" />
          <v-alert v-if="errorMessage" type="error" density="compact" closable class="mb-4" :text="errorMessage" @click:close="errorMessage = ''" />
          <v-progress-linear v-if="isLoading" indeterminate color="indigo" />

          <template v-if="stock">
            <!-- 店舗と内訳 -->
            <LabelMetaStrip :items="infoItems" class="mb-6">
              <template #remaining>
                <span class="font-weight-bold mr-2" :class="stock.remainingQty < 0 ? 'text-red-darken-1' : 'text-blue-darken-2'">
                  {{ num(stock.remainingQty) }} {{ $t('labelStock.label.unitSheet') }}
                </span>
                <v-chip size="x-small" label variant="tonal" :color="remainColor(stock.remainingQty)">
                  {{ $t('labelStock.label.state.' + remainState(stock.remainingQty)) }}
                </v-chip>
              </template>
            </LabelMetaStrip>

            <!-- 変更履歴（納品・出荷・修正を1つの表で時系列に） -->
            <div class="d-flex align-end mb-2">
                <div>
                  <div class="text-subtitle-1 font-weight-bold">{{ $t('labelStock.label.ledger') }}</div>
                  <div class="text-caption text-medium-emphasis">{{ $t('labelStock.label.ledgerNote') }}</div>
                </div>
                <v-spacer></v-spacer>
                <v-btn variant="tonal" color="indigo" @click="openAdjust">{{ $t('labelStock.action.adjust') }}</v-btn>
            </div>
              <div class="d-flex flex-wrap align-center mb-2 text-caption text-medium-emphasis" style="gap: 8px 16px;">
                <span v-for="k in kinds" :key="k" class="d-flex align-center">
                  <v-chip size="x-small" label variant="tonal" class="mr-2" :color="kindColor[k]">{{ $t('labelStock.label.kind.' + k) }}</v-chip>
                  {{ $t('labelStock.label.legend.' + k) }}
                </span>
              </div>
              <v-table density="compact" class="text-no-wrap">
                <thead>
                  <tr>
                    <th>{{ $t('labelStock.label.ledgerTable.date') }}</th>
                    <th>{{ $t('labelStock.label.ledgerTable.kind') }}</th>
                    <th>{{ $t('labelStock.label.ledgerTable.content') }}</th>
                    <th class="text-right">{{ $t('labelStock.label.ledgerTable.qty') }}</th>
                    <th class="text-right">{{ $t('labelStock.label.ledgerTable.balance') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(e, i) in ledger" :key="i" :class="e.kind === 'PLANNED' ? 'bg-grey-lighten-5' : ''">
                    <td class="text-medium-emphasis">
                      {{ displayDate(e) }}
                      <span v-if="e.kind === 'SHIPMENT'" class="text-caption text-disabled ml-1">{{ $t('labelStock.label.monthly') }}</span>
                    </td>
                    <td>
                      <v-chip size="x-small" label variant="tonal" :color="kindColor[e.kind]" style="min-width: 44px;" class="justify-center">
                        {{ $t('labelStock.label.kind.' + e.kind) }}
                      </v-chip>
                    </td>
                    <td>
                      <div class="font-weight-medium">{{ content(e) }}</div>
                      <div v-if="subContent(e)" class="text-caption text-medium-emphasis">{{ subContent(e) }}</div>
                    </td>
                    <td class="text-right font-weight-bold" :class="e.kind === 'PLANNED' ? 'text-disabled' : (e.qty > 0 ? 'text-blue-darken-2' : 'text-red-darken-1')">
                      {{ e.kind === 'PLANNED' ? '(' + signed(e.qty) + ')' : signed(e.qty) }}
                    </td>
                    <td class="text-right">
                      <span v-if="e.balance == null" class="text-caption text-disabled">{{ $t('labelStock.label.notCounted') }}</span>
                      <span v-else class="font-weight-bold">{{ num(e.balance) }}</span>
                    </td>
                  </tr>
                  <tr v-if="ledger.length === 0">
                    <td colspan="5" class="text-center text-medium-emphasis">
                      {{ $t('common.label.all') }} 0 {{ $t('common.label.totalCountOf') }}
                    </td>
                  </tr>
                </tbody>
              </v-table>
          </template>
        </v-container>

        <!-- 残数の修正 -->
        <v-dialog v-model="adjustDialog" width="560" persistent>
          <v-card v-if="stock">
            <v-card-title>{{ $t('labelStock.dialog.adjustTitle') }}</v-card-title>
            <v-divider></v-divider>
            <v-card-text>
              <div class="text-body-2 mb-4">{{ $t('labelStock.dialog.adjustText') }}</div>
              <v-alert v-if="adjustError" type="error" density="compact" class="mb-4" :text="adjustError" />
              <div class="d-flex align-end" style="gap: 12px;">
                <div class="flex-grow-1">
                  <div class="text-caption text-medium-emphasis font-weight-bold mb-1">{{ $t('labelStock.label.systemRemaining') }}</div>
                  <v-sheet class="pa-2 text-right text-subtitle-1 font-weight-bold bg-grey-lighten-4 rounded">{{ num(stock.remainingQty) }}</v-sheet>
                </div>
                <div class="pb-2 text-medium-emphasis">→</div>
                <div class="flex-grow-1">
                  <v-text-field
                    v-model="actualQty"
                    type="number"
                    min="0"
                    :label="$t('labelStock.label.actualRemaining') + ' *'"
                    variant="outlined"
                    density="compact"
                    hide-details
                    class="text-right-input"
                  />
                </div>
              </div>
              <v-sheet class="mt-4 pa-3 rounded" :class="adjustDiff === 0 ? 'bg-grey-lighten-4' : (adjustDiff > 0 ? 'bg-blue-lighten-5' : 'bg-red-lighten-5')">
                <div class="text-caption text-medium-emphasis">{{ $t('labelStock.label.adjustValue') }}</div>
                <div class="text-h6" :class="adjustDiff === 0 ? 'text-medium-emphasis' : (adjustDiff > 0 ? 'text-blue-darken-2' : 'text-red-darken-1')">
                  {{ signed(adjustDiff) }} {{ $t('labelStock.label.unitSheet') }}
                </div>
              </v-sheet>
              <v-text-field
                v-model="adjustReason"
                :label="$t('labelStock.label.reason') + ' *'"
                :placeholder="$t('labelStock.label.reasonPlaceholder')"
                variant="outlined"
                density="compact"
                hide-details="auto"
                counter="200"
                class="mt-4"
              />
            </v-card-text>
            <v-divider></v-divider>
            <v-card-actions class="pa-4">
              <v-spacer></v-spacer>
              <v-btn variant="text" :disabled="isSubmitting" @click="adjustDialog = false">{{ $t('labelStock.action.back') }}</v-btn>
              <v-btn class="px-6 bg-indigo-darken-1" elevation="2" :loading="isSubmitting" @click="doAdjust">{{ $t('labelStock.action.doAdjust') }}</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-main>
    </AppWrapperView>
  </v-app>
</template>

<script lang="js">
import AppWrapperView from "@/components/views/AppWrapperView";
import LabelMetaStrip from "@/components/label/LabelMetaStrip";
import api from "@/apis/staff";
import router from "@/router/index";
import Paths from "@/router/indexPaths";
import { num, signed, remainState, remainColor, LEDGER_KIND_COLOR } from "@/utils/labelManagement";

export default {
  components: { AppWrapperView, LabelMetaStrip },
  data: () => ({
    kinds: ["DELIVERY", "SHIPMENT", "ADJUSTMENT", "PLANNED"],
    kindColor: LEDGER_KIND_COLOR,
    stock: null,
    ledger: [],
    isLoading: false,
    flash: "",
    errorMessage: "",
    adjustDialog: false,
    actualQty: "",
    adjustReason: "",
    adjustError: "",
    isSubmitting: false,
  }),
  computed: {
    infoItems() {
      const st = this.stock;
      const t = (k) => this.$t("labelStock.label." + k);
      const sheet = " " + t("unitSheet");
      return [
        { label: t("store"), value: st.siteCode + "  " + st.storeName, strong: true, wide: true },
        { label: t("janCode"), value: st.janCode },
        { label: t("orderedTotal"), value: num(st.orderedQty) + sheet },
        { label: t("shippedTotal"), value: "-" + num(st.shippedQty) + sheet },
        { label: t("adjustTotal"), value: st.adjustQty === 0 ? "—" : signed(st.adjustQty) + sheet },
        { label: t("remaining"), slot: "remaining" },
      ];
    },
    adjustDiff() {
      if (this.actualQty === "" || this.actualQty == null || !this.stock) return 0;
      return Number(this.actualQty) - this.stock.remainingQty;
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    num,
    signed,
    remainState,
    remainColor,
    load() {
      const siteCode = this.$route.query.siteCode;
      if (!siteCode) {
        this.backToList();
        return;
      }
      this.isLoading = true;
      api.post("/label-stock/detail", { siteCode })
        .then((response) => {
          if (response.data?.status == 1) {
            this.errorMessage = response.data.errorMessage;
            this.stock = null;
            return;
          }
          this.stock = response.data.results.stock;
          this.ledger = response.data.results.ledgerList ?? [];
        })
        .catch(() => {
          this.stock = null;
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    displayDate(e) {
      return (e.date ?? "").replace(/-/g, "/");
    },
    content(e) {
      if (e.kind === "SHIPMENT") {
        return this.$t("labelStock.label.content.SHIPMENT", [parseInt(e.date.slice(5, 7), 10)]);
      }
      return this.$t("labelStock.label.content." + e.kind);
    },
    subContent(e) {
      if (e.kind === "SHIPMENT") return this.$t("labelStock.label.boxesSuffix", [num(-e.qty)]);
      if (e.kind === "ADJUSTMENT") return [e.reason, e.userName].filter((x) => x).join(" / ");
      return e.requestId ?? "";
    },
    openAdjust() {
      this.actualQty = this.stock.remainingQty;
      this.adjustReason = "";
      this.adjustError = "";
      this.adjustDialog = true;
    },
    doAdjust() {
      this.isSubmitting = true;
      this.adjustError = "";
      const diff = this.adjustDiff;
      api.post("/label-stock/adjust", {
        siteCode: this.stock.siteCode,
        actualQty: this.actualQty === "" ? null : Number(this.actualQty),
        reason: this.adjustReason,
      })
        .then((response) => {
          if (response.data?.status == 1) {
            this.adjustError = response.data.errorMessage;
            return;
          }
          this.adjustDialog = false;
          this.flash = this.$t("labelStock.message.adjusted", [signed(diff)]);
          this.load();
        })
        .catch(() => {
          this.adjustError = this.$t("common.message.error");
        })
        .finally(() => {
          this.isSubmitting = false;
        });
    },
    backToList() {
      router.push({ name: Paths.LABEL_STOCK });
    },
  },
};
</script>

<style scoped>
.text-right-input :deep(input) {
  text-align: right;
}
</style>
